import {
  CheckCheckIcon,
  ExternalLinkIcon,
  FileTextIcon,
  MoreVerticalIcon,
  PencilIcon,
  RotateCcwIcon,
  SendIcon,
  Trash2Icon,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Boton } from '@/components/ui/Boton'
import { ChipEstado } from '@/components/ui/ChipEstado'
import {
  Dialogo,
  DialogoContenido,
  DialogoCuerpo,
  DialogoDescripcion,
  DialogoEncabezado,
  DialogoTitulo,
} from '@/components/ui/Dialogo'
import {
  MenuContenido,
  MenuDesplegable,
  MenuDisparador,
  MenuItem,
} from '@/components/ui/MenuDesplegable'
import { TextoConFormato } from '@/components/ui/TextoConFormato'
import { useAvisar } from '@/components/ui/avisador-contexto'
import { Confirmacion } from '@/features/admin/Confirmacion'
import { mensajeDeError } from '@/features/admin/ModalMarco'
import { formatearPeriodo } from '@/features/grilla/formato'
import { HiloComentarios } from '@/features/publicacion/HiloComentarios'
import { cn } from '@/lib/cn'
import {
  franjaDePlanificacion,
  type PanelPlanificacion as Panel,
} from '@/lib/schemas/planificacion'
import { DESCARTAR_COMENTARIO, SIN_COMENTARIO } from '@/lib/schemas/revision'
import {
  useAccionPlanificacion,
  useComentarPlanificacion,
  usePanelPlanificacion,
  useResolverComentarioPlan,
} from './api'

interface PanelPlanificacionProps {
  planificacionId: string
  esRastro: boolean
  alCerrar: () => void
  alEditar: (p: Panel) => void
  alDarDeBaja: (p: Panel) => void
}

/**
 * C10 · panel de planificación (Fase 5a). El mismo modal que el de una
 * publicación, con la columna izquierda reemplazada: descripción y el
 * documento de Google Docs, que se abre en otra pestaña (sin "Editar acá":
 * el documento embebido queda gris con una cuenta de trabajo abierta).
 */
export function PanelPlanificacion(props: PanelPlanificacionProps) {
  const [borrador, setBorrador] = useState('')
  const [confirmandoSalida, setConfirmandoSalida] = useState(false)
  const panel = usePanelPlanificacion(props.planificacionId)
  const accion = useAccionPlanificacion()

  // Abrirla le quita la marca de "nuevo" a quien la abre.
  useEffect(() => {
    accion.mutate({ id: props.planificacionId, accion: 'visita' })
    // Una sola vez por apertura.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.planificacionId])

  const intentarCerrar = () => {
    if (borrador.trim()) setConfirmandoSalida(true)
    else props.alCerrar()
  }

  return (
    <>
      <Dialogo
        open
        onOpenChange={(abierto) => {
          if (!abierto) intentarCerrar()
        }}
      >
        <DialogoContenido tamano="grande" aria-busy={panel.isPending}>
          {panel.data ? (
            <Contenido
              {...props}
              panel={panel.data}
              borrador={borrador}
              alEscribir={setBorrador}
            />
          ) : (
            <>
              <DialogoEncabezado>
                <DialogoTitulo className="text-xl font-semibold">
                  Planificación
                </DialogoTitulo>
                <DialogoDescripcion className="sr-only">Cargando</DialogoDescripcion>
              </DialogoEncabezado>
              <DialogoCuerpo>
                {panel.isError ? (
                  <div role="alert" className="flex flex-col items-start gap-3">
                    <p className="text-sm text-tinta-suave">
                      No pudimos abrir la planificación.
                    </p>
                    <Boton
                      variante="secundario"
                      tamano="chico"
                      onClick={() => {
                        void panel.refetch()
                      }}
                    >
                      Reintentar
                    </Boton>
                  </div>
                ) : (
                  <p className="text-sm text-tinta-suave">Cargando…</p>
                )}
              </DialogoCuerpo>
            </>
          )}
        </DialogoContenido>
      </Dialogo>

      <Confirmacion
        abierto={confirmandoSalida}
        alCerrar={() => {
          setConfirmandoSalida(false)
        }}
        titulo="Comentario sin enviar"
        texto={DESCARTAR_COMENTARIO}
        confirmar="Descartar"
        peligro
        alConfirmar={() => {
          setConfirmandoSalida(false)
          props.alCerrar()
          return Promise.resolve()
        }}
      />
    </>
  )
}

function Contenido({
  panel,
  esRastro,
  alCerrar,
  alEditar,
  alDarDeBaja,
  borrador,
  alEscribir,
}: PanelPlanificacionProps & {
  panel: Panel
  borrador: string
  alEscribir: (t: string) => void
}) {
  const avisar = useAvisar()
  const accion = useAccionPlanificacion()
  const comentar = useComentarPlanificacion(panel.id)
  const resolver = useResolverComentarioPlan()
  const [error, setError] = useState<string | null>(null)

  const franja = franjaDePlanificacion(panel.estado, esRastro)
  const puedeDecidir = !esRastro && panel.estado === 'para_revisar' && !panel.dadaDeBaja

  const hacer = (
    a: Parameters<typeof accion.mutateAsync>[0]['accion'],
    aviso: string,
  ) => {
    setError(null)
    accion
      .mutateAsync({ id: panel.id, accion: a })
      .then(() => {
        avisar(aviso)
      })
      .catch((e: unknown) => {
        setError(mensajeDeError(e))
      })
  }

  const decidir = (a: 'aprobar' | 'cambios') => {
    setError(null)
    accion
      .mutateAsync({ id: panel.id, accion: a })
      .then(() => {
        avisar(
          a === 'aprobar'
            ? 'Planificación aprobada'
            : 'Pedido de cambios enviado a RASTRO',
          {
            accion: {
              etiqueta: 'Deshacer',
              alHacer: () => {
                void accion.mutateAsync({ id: panel.id, accion: 'deshacer' })
              },
            },
          },
        )
        alCerrar()
      })
      .catch((e: unknown) => {
        setError(mensajeDeError(e))
      })
  }

  return (
    <>
      <DialogoEncabezado className="flex-row items-start justify-between gap-3">
        <div className="min-w-0">
          <DialogoTitulo className="text-xl font-semibold tracking-tight">
            {panel.titulo}
          </DialogoTitulo>
          <DialogoDescripcion className="text-sm text-tinta-suave">
            {formatearPeriodo(panel.fechaDesde, panel.fechaHasta)}
          </DialogoDescripcion>
        </div>
        {esRastro && panel.dadaDeBaja && (
          <Boton
            variante="secundario"
            tamano="chico"
            className="shrink-0"
            onClick={() => {
              hacer('restaurar', 'Planificación restaurada')
            }}
          >
            <RotateCcwIcon className="size-4" aria-hidden />
            Restaurar
          </Boton>
        )}
        {esRastro && !panel.dadaDeBaja && (
          <div className="flex shrink-0 items-center gap-1">
            <Boton
              variante="secundario"
              tamano="chico"
              onClick={() => {
                alEditar(panel)
              }}
            >
              <PencilIcon className="size-4" aria-hidden />
              Editar
            </Boton>
            <MenuDesplegable>
              <MenuDisparador asChild>
                <Boton variante="sutil" tamano="icono" aria-label="Más acciones">
                  <MoreVerticalIcon className="size-5" aria-hidden />
                </Boton>
              </MenuDisparador>
              <MenuContenido>
                {panel.estado === 'borrador' && (
                  <MenuItem
                    onSelect={() => {
                      hacer('enviar', 'Planificación enviada a revisión')
                    }}
                  >
                    <SendIcon className="size-4" aria-hidden />
                    Enviar a revisión
                  </MenuItem>
                )}
                <MenuItem
                  peligro
                  onSelect={() => {
                    alDarDeBaja(panel)
                  }}
                >
                  <Trash2Icon className="size-4" aria-hidden />
                  Dar de baja
                </MenuItem>
              </MenuContenido>
            </MenuDesplegable>
          </div>
        )}
      </DialogoEncabezado>

      <DialogoCuerpo className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)] lg:gap-8">
        <div className="flex flex-col gap-4">
          {panel.descripcion ? (
            <TextoConFormato texto={panel.descripcion} />
          ) : (
            <p className="text-sm text-tinta-suave">Sin descripción.</p>
          )}

          <a
            href={panel.urlDocumento}
            target="_blank"
            rel="noopener noreferrer"
            data-documento
            className="flex flex-wrap items-center gap-3 rounded-tarjeta border border-borde bg-lienzo p-4 hover:bg-tarjeta focus-visible:outline-2 focus-visible:outline-tinta"
          >
            <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-borrador text-borrador-tinta">
              <FileTextIcon className="size-5" aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{panel.titulo}</span>
              <span className="block text-xs text-tinta-suave">Documento principal</span>
            </span>
            <span className="flex w-full items-center gap-1.5 text-sm font-medium sm:w-auto sm:shrink-0">
              Abrir en Google Docs
              <ExternalLinkIcon className="size-4" aria-hidden />
            </span>
          </a>
        </div>

        <div className="mt-6 flex flex-col gap-4 lg:mt-0">
          <div className="flex flex-wrap items-center gap-2">
            <ChipEstado estado={panel.estado} paraRastro={esRastro} />
            {panel.dadaDeBaja && (
              <span className="text-sm text-tinta-suave">Dada de baja</span>
            )}
          </div>
          <p className="text-sm text-tinta-suave">{panel.contexto}</p>

          {franja && (
            <p
              data-franja
              className={cn(
                'rounded-2xl px-4 py-3 text-sm',
                panel.estado === 'cambios'
                  ? 'bg-cambios text-cambios-tinta'
                  : 'bg-lienzo text-tinta',
              )}
            >
              {franja}
            </p>
          )}

          {esRastro && panel.estado === 'cambios' && !panel.dadaDeBaja && (
            <div className="flex flex-col gap-1.5">
              <Boton
                disabled={accion.isPending}
                onClick={() => {
                  hacer('corregida', 'Planificación aprobada con los cambios aplicados')
                }}
              >
                <CheckCheckIcon className="size-4" aria-hidden />
                Marcar como corregida
              </Boton>
              <p className="text-xs text-tinta-suave">
                Al marcarla, la planificación queda aprobada.
              </p>
            </div>
          )}

          <HiloComentarios
            comentarios={panel.comentariosLista.map((c) => ({
              ...c,
              laminaNro: null,
              versionNumero: 1,
            }))}
            versionActual={1}
            esRastro={esRastro}
            laminaVisible={null}
            tipo="imagen"
            borrador={borrador}
            alEscribir={alEscribir}
            enviando={comentar.isPending}
            alEnviar={() => {
              setError(null)
              comentar
                .mutateAsync(borrador)
                .then(() => {
                  alEscribir('')
                })
                .catch((e: unknown) => {
                  setError(mensajeDeError(e))
                })
            }}
            alMarcarResuelto={(comentarioId, resuelto) => {
              void resolver.mutateAsync({ comentarioId, resuelto })
            }}
            deshabilitado={panel.dadaDeBaja}
          />

          {error && (
            <p
              role="alert"
              className="rounded-xl bg-nuevo/10 px-3.5 py-2.5 text-sm text-nuevo"
            >
              {error}
            </p>
          )}
        </div>
      </DialogoCuerpo>

      {puedeDecidir && (
        <div className="mt-auto flex shrink-0 flex-col gap-2 border-t border-borde px-4 py-3 sm:flex-row sm:items-center sm:justify-end sm:px-6">
          {!panel.comentoEnEstaRonda && (
            <p className="text-xs text-tinta-suave sm:mr-auto">{SIN_COMENTARIO}</p>
          )}
          <Boton
            variante="secundario"
            className="min-h-12 w-full sm:min-h-11 sm:w-auto"
            disabled={!panel.comentoEnEstaRonda || accion.isPending}
            onClick={() => {
              decidir('cambios')
            }}
          >
            Pedir cambios
          </Boton>
          <Boton
            className="min-h-12 w-full sm:min-h-11 sm:w-auto"
            disabled={accion.isPending}
            onClick={() => {
              decidir('aprobar')
            }}
          >
            Aprobar
          </Boton>
        </div>
      )}
    </>
  )
}
